import {
  SET_ALERT,
  UPDATE_ALERT_FROM_STREAM,
  REMOVE_ALERT,
  RECONCILE_ALERTS,
} from "../actions/index";
import { reconcileById } from "./reconcile";

/*
 * Active alarms are stored grouped by gateway then device:
 *   byId[gatewayGuid][deviceGuid] = [ threshold, ... ]
 * There is intentionally NO aggregate `total` counter: every consumer derives
 * the count from this structure (utils.countAlertsForCustomer), so a single
 * source of truth avoids the counter drifting out of sync with byId.
 */
const initialState = { byId: {}, allIds: [] };

// Add an ON alert (idempotent: a replayed ON for the same threshold is a no-op).
const addAlert = (state, gatewayGuid, deviceGuid, th) => {
  const existing = state.byId[gatewayGuid]?.[deviceGuid] || [];
  if (existing.some((a) => a.thresholdGuid === th.thresholdGuid)) return state;
  return {
    ...state,
    byId: {
      ...state.byId,
      [gatewayGuid]: {
        ...state.byId[gatewayGuid],
        [deviceGuid]: [...existing, th],
      },
    },
    allIds: state.allIds.includes(gatewayGuid)
      ? state.allIds
      : [...state.allIds, gatewayGuid],
  };
};

// Remove one threshold, reference-preserving, pruning empty device/gateway.
const removeAlert = (state, gatewayGuid, deviceGuid, thresholdGuid) => {
  const deviceAlerts = state.byId[gatewayGuid]?.[deviceGuid];
  if (!deviceAlerts?.some((a) => a.thresholdGuid === thresholdGuid)) return state;

  const remaining = deviceAlerts.filter(
    (a) => a.thresholdGuid !== thresholdGuid
  );
  if (remaining.length > 0) {
    return {
      ...state,
      byId: {
        ...state.byId,
        [gatewayGuid]: { ...state.byId[gatewayGuid], [deviceGuid]: remaining },
      },
    };
  }

  const nextDevices = { ...state.byId[gatewayGuid] };
  delete nextDevices[deviceGuid];
  if (Object.keys(nextDevices).length > 0) {
    return { ...state, byId: { ...state.byId, [gatewayGuid]: nextDevices } };
  }

  const nextById = { ...state.byId };
  delete nextById[gatewayGuid];
  return {
    ...state,
    byId: nextById,
    allIds: state.allIds.filter((id) => id !== gatewayGuid),
  };
};

// Remove a threshold wherever it appears (user-initiated threshold delete).
const removeAlertEverywhere = (state, thresholdGuid) => {
  let changed = false;
  const nextById = {};
  for (const gwGuid of state.allIds) {
    const devices = state.byId[gwGuid] || {};
    const nextDevices = {};
    for (const dvGuid of Object.keys(devices)) {
      const filtered = devices[dvGuid].filter(
        (a) => a.thresholdGuid !== thresholdGuid
      );
      if (filtered.length !== devices[dvGuid].length) changed = true;
      if (filtered.length > 0) nextDevices[dvGuid] = filtered;
    }
    if (Object.keys(nextDevices).length > 0) nextById[gwGuid] = nextDevices;
  }
  if (!changed) return state;
  return {
    ...state,
    byId: nextById,
    allIds: state.allIds.filter((id) => id in nextById),
  };
};

const handleStream = (state, payload) => {
  const { gatewayGuid, deviceGuid } = payload;
  const th = { ...payload };
  delete th.deviceLastSnapshot;
  delete th.gatewayGuid;
  return th.alarmType?.toUpperCase() === "ON"
    ? addAlert(state, gatewayGuid, deviceGuid, th)
    : removeAlert(state, gatewayGuid, deviceGuid, th.thresholdGuid);
};

const alerts = (state = initialState, action) => {
  switch (action.type) {
    case SET_ALERT:
      return action.payload;
    case RECONCILE_ALERTS:
      return reconcileById(state, action.payload);
    case UPDATE_ALERT_FROM_STREAM:
      return handleStream(state, action.payload);
    case REMOVE_ALERT:
      return removeAlertEverywhere(state, action.payload);
    default:
      return state;
  }
};

export default alerts;
